import React, { useState, useEffect, useRef } from 'react';
import { fetchWithCorpId, asArray } from '../utils/api';
import { timesheetNo } from '../utils/timesheet';

// 1 -> "1st", 2 -> "2nd", 11 -> "11th"
const ordinal = (n) => {
  const num = parseInt(n, 10);
  if (!Number.isFinite(num)) return `${n}`;
  const mod100 = num % 100;
  const suffix = mod100 >= 11 && mod100 <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[num % 10] || 'th');
  return `${num}${suffix}`;
};
import { useParams } from 'react-router-dom';
import { Section, Field, FormLoading } from './ui/formkit';
import { useToast } from './ui/Toast';
import { toDateInputValue } from '../utils/dateUtils';

const DEFAULT_TIMESHEET = {
  id: '', employeeId: '', clientId: '',
  periodStartDate: '', periodEndDate: '',
  totalHours: '', status: 'Pending',
  billingCode: '', notes: '',
};

function TimesheetForm({
  mode = 'create',
  initialData = null,
  timesheetId,
  formRef,
  hideActions = false,
  onSuccess,
  onCancel,
}) {
  const params = useParams();
  const toast = useToast();
  const id = timesheetId ?? params.id;
  const isEdit = mode === 'edit';
  const [formData, setFormData] = useState(DEFAULT_TIMESHEET);
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [message, setMessage] = useState('');
  const [loadingRecord, setLoadingRecord] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  // Snapshot of the last saved state; drives the drawer's discard guard.
  // In edit mode it is set once the record loads.
  const initialSnapshotRef = useRef(isEdit ? null : JSON.stringify(DEFAULT_TIMESHEET));

  useEffect(() => {
    if (initialData) {
      const normalized = {
        ...DEFAULT_TIMESHEET,
        ...initialData,
        periodStartDate: toDateInputValue(initialData.periodStartDate),
        periodEndDate: toDateInputValue(initialData.periodEndDate),
      };
      setFormData(normalized);
      initialSnapshotRef.current = JSON.stringify(normalized);
    }
  }, [initialData]);

  useEffect(() => {
    if (!isEdit || !id) {
      setLoadingRecord(false);
      return;
    }
    let cancelled = false;
    setLoadingRecord(true);
    fetchWithCorpId(`/timesheets/${id}`)
      .then((data) => {
        if (cancelled || !data) return;
        const normalized = {
          ...DEFAULT_TIMESHEET,
          ...data,
          periodStartDate: toDateInputValue(data.periodStartDate),
          periodEndDate: toDateInputValue(data.periodEndDate),
        };
        setFormData(normalized);
        initialSnapshotRef.current = JSON.stringify(normalized);
      })
      .catch((error) => {
        if (!cancelled) setMessage('Could not load this timesheet: ' + error.message);
      })
      .finally(() => {
        if (!cancelled) setLoadingRecord(false);
      });
    return () => { cancelled = true; };
  }, [isEdit, id]);

  useEffect(() => {
    Promise.all([
      fetchWithCorpId('/employees?page=0&size=99999&sort=fullName'),
      fetchWithCorpId('/clients?page=0&size=99999&sort=name'),
    ]).then(([e, c]) => {
      setEmployees(asArray(e));
      setClients(asArray(c));
    }).catch(() => {});
  }, []);

  const isDirty = initialSnapshotRef.current !== null
    && JSON.stringify(formData) !== initialSnapshotRef.current;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setMessage('');
    setSaving(true);
    try {
      const url = isEdit && formData.id ? `/timesheets/${formData.id}` : '/timesheets';
      const method = isEdit ? 'PUT' : 'POST';
      await fetchWithCorpId(url, { method, body: JSON.stringify(formData) });
      initialSnapshotRef.current = JSON.stringify(formData);
      toast.success(isEdit ? 'Timesheet updated' : 'Timesheet created');
      onSuccess?.();
    } catch (error) {
      setMessage(error.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loadingRecord) {
    return <FormLoading label="Loading timesheet…" />;
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit} data-dirty={isDirty ? 'true' : 'false'}>
      {message && (
        <div className="alert error" role="alert" style={{ marginBottom: '20px' }}>{message}</div>
      )}

      <Section title="Timesheet" description="Identifier and the people involved.">
        <Field label="Timesheet">
          <input type="text" name="timesheetNo"
            value={formData.id ? (timesheetNo(formData) ? `${ordinal(timesheetNo(formData))} timesheet for this billing` : 'Created manually') : ''}
            readOnly
            title={formData.id ? `Internal id: ${formData.id}` : ''}
            style={{ background: 'var(--surface-soft)', cursor: 'not-allowed', color: 'var(--muted)' }} />
        </Field>
        <Field label="Status" required>
          <select name="status" value={formData.status} onChange={handleChange} required>
            <option value="Pending">Pending</option>
            <option value="Submitted">Submitted</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>
        </Field>
        <Field label="Employee" required>
          <select name="employeeId" value={formData.employeeId} onChange={handleChange} required>
            <option value="">Select employee</option>
            {employees.map(e => <option key={e.id} value={e.id}>{e.fullName}</option>)}
          </select>
        </Field>
        <Field label="Client" required>
          <select name="clientId" value={formData.clientId} onChange={handleChange} required>
            <option value="">Select client</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
      </Section>

      <Section title="Period" description="Start, end, and total hours worked.">
        <Field label="Period start" required>
          <input type="date" name="periodStartDate" value={formData.periodStartDate} onChange={handleChange} required />
        </Field>
        <Field label="Period end" required>
          <input type="date" name="periodEndDate" value={formData.periodEndDate} onChange={handleChange} required />
        </Field>
        <Field label="Total hours" required>
          <input type="number" step="0.01" min="0" name="totalHours" value={formData.totalHours} onChange={handleChange} required />
        </Field>
        <Field label="Billing code" required hint="Must match the billing code set up for this employee and client.">
          <input type="text" name="billingCode" value={formData.billingCode} onChange={handleChange} required />
        </Field>
      </Section>

      <Section title="Notes" description="Optional context." grid="1fr">
        <Field label="Notes">
          <textarea name="notes" value={formData.notes} onChange={handleChange} rows={4} />
        </Field>
      </Section>

      {!hideActions && (
        <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          {onCancel && (
            <button type="button" onClick={onCancel} className="btn-secondary" disabled={saving}>Cancel</button>
          )}
        </div>
      )}
    </form>
  );
}

export default TimesheetForm;
